import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

const CartContext = createContext(null);
const STORAGE_VERSION = 1;

function storageKey(storeSlug) {
  return `atomicpay-storefront-cart:v${STORAGE_VERSION}:${storeSlug}`;
}

function readCart(storeSlug) {
  try {
    const value = JSON.parse(localStorage.getItem(storageKey(storeSlug)));
    if (!Array.isArray(value?.items)) return [];
    return value.items.flatMap((item) => {
      const quantity = boundedQuantity(item.quantity, item.maxQuantity);
      return quantity > 0 ? [{ ...item, quantity }] : [];
    });
  } catch {
    return [];
  }
}

function cartItem(product, variant) {
  const availableQuantity = Number(variant.availableQuantity);
  return {
    id: `${product.id}:${variant.id}`,
    productId: product.id,
    productHandle: product.handle,
    variantId: variant.id,
    title: product.title,
    variantTitle: variant.title,
    options: variant.options || {},
    sku: variant.sku || null,
    image:
      product.primaryImage ||
      product.images?.find((image) => image.isPrimary)?.url ||
      product.images?.[0]?.url ||
      null,
    amountMinor: Number(variant.amountMinor),
    currency: variant.currency || "BRL",
    stockStatus: variant.stockStatus,
    maxQuantity:
      variant.stockStatus === "in_stock" && availableQuantity > 0
        ? availableQuantity
        : null,
    quantity: 1,
  };
}

function boundedQuantity(quantity, maxQuantity) {
  const normalized = Math.max(0, Math.trunc(Number(quantity) || 0));
  return maxQuantity == null
    ? normalized
    : Math.min(normalized, maxQuantity);
}

export function CartProvider({ storeSlug, children }) {
  const [items, setItems] = useState(() => readCart(storeSlug));

  useEffect(() => {
    localStorage.setItem(
      storageKey(storeSlug),
      JSON.stringify({ items, updatedAt: new Date().toISOString() })
    );
  }, [items, storeSlug]);

  const addItem = useCallback((product, variant) => {
    if (!product || !variant || variant.stockStatus === "out_of_stock") {
      return false;
    }
    const nextItem = cartItem(product, variant);
    setItems((current) => {
      const existing = current.find((item) => item.id === nextItem.id);
      if (!existing) return [...current, nextItem];
      return current.map((item) =>
        item.id === nextItem.id
          ? {
              ...item,
              ...nextItem,
              quantity: boundedQuantity(
                item.quantity + 1,
                nextItem.maxQuantity
              ),
            }
          : item
      );
    });
    return true;
  }, []);

  const setQuantity = useCallback((itemId, quantity) => {
    setItems((current) =>
      current.flatMap((item) => {
        if (item.id !== itemId) return [item];
        const nextQuantity = boundedQuantity(quantity, item.maxQuantity);
        return nextQuantity > 0 ? [{ ...item, quantity: nextQuantity }] : [];
      })
    );
  }, []);

  const removeItem = useCallback((itemId) => {
    setItems((current) => current.filter((item) => item.id !== itemId));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const value = {
    items,
    itemCount: items.reduce((total, item) => total + item.quantity, 0),
    subtotalMinor: items.reduce(
      (total, item) => total + item.amountMinor * item.quantity,
      0
    ),
    currency: items[0]?.currency || "BRL",
    addItem,
    setQuantity,
    removeItem,
    clearCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used inside CartProvider");
  }
  return context;
}
